import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogTitle, LinearProgress, Tooltip, IconButton, TextField } from "@mui/material";
import { Formik, Form, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { Add, Close, Edit } from '@mui/icons-material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import FormikControl from '../../../../../../components/FormikControl/FormikControl';
import voucherServices from '../voucherServices';
import { useAlert } from '../../../../../../context/AlertContext';
import { useAppData } from '../../../../../../context/AppDataContext';

// --- 1. THE OBSERVER COMPONENT ---
// This component has access to the Formik context and handles the math
const ExpenseCalculationObserver = () => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
        const taxable = Number(values.taxableTotal) || 0;
        const rate = Number(values.gstRate) || 0;
        
        const gst = (taxable * rate) / 100;
        const exactTotal = taxable + gst;
        
        const roundedTotal = Math.round(exactTotal);
        const roundOff = (roundedTotal - exactTotal).toFixed(2);

        // We use a timeout or a check to prevent "render-phase" updates
        setFieldValue('gstTotal', gst.toFixed(2));
        setFieldValue('roundOff', roundOff);
        setFieldValue('grandTotal', roundedTotal);
        
    }, [values.taxableTotal, values.gstRate, setFieldValue]);

    return null; // This component doesn't render anything
};

function ExpenseVoucherForm({ data, callBack }) {
    const { AppAlert } = useAlert();
    const { ledgerDD, companyLocationDD } = useAppData();
    const [dialogStatus, setDialogStatus] = useState(false);
    const [savingInProgress, setSavingStatus] = useState(false);
    const [formValues, setFormValues] = useState(null);
    const navigate = useNavigate();

    const expenseLedgers = ledgerDD.filter(l => l.groupTitle === 'Indirect Expenses');
    const bankCashLedgers = ledgerDD.filter(l => 
        ['Bank Accounts', 'Cash-in-Hand', 'Bank OCC A/c', 'Bank OD A/c'].includes(l.groupTitle)
    );

    const initialValues = {
        voucherType: "Expense",
        voucherNo: "",
        refNo: "", 
        voucherDate: moment().format(moment.HTML5_FMT.DATE),
        ledgerId: "",        
        paymentLedgerId: "", 
        employeeName: "", 
        locationId: "", 
        taxableTotal: 0,
        gstRate: 0,
        gstTotal: 0,
        roundOff: 0,
        grandTotal: 0,
        narration: "",
    };


        const updateData = ()=>{
            let autoFill = {
                id:data.id,
                voucherType: data.voucherType,      
                voucherNo: data.voucherNo,
                refNo:data.refNo,
                voucherDate: data.voucherDate,
                ledgerId: data.ledgerId,
                paymentLedgerId: data.paymentLedgerId,
                employeeName:data.employeeName,
                locationId:data.locationId,
                taxableTotal: data.taxableTotal,
                gstRate: data.gstRate,
                gstTotal: data.gstTotal,
                roundOff: data.roundOff,
                grandTotal: data.grandTotal,
                narration: data.narration,
            }
            setFormValues(autoFill);
            setDialogStatus(true)
        }
        const addNew = ()=>{
            setFormValues(initialValues);
            setDialogStatus(true)
        }

    const validationRule = Yup.object({
        //voucherNo: Yup.string().required("Required"),
        voucherDate: Yup.string().required("Required"),
        locationId: Yup.string().required("Required"),
        ledgerId: Yup.string().required("Select Category"),
        paymentLedgerId: Yup.string().required("Select Payment Source"),
        employeeName: Yup.string().required("Required"),
    
        taxableTotal: Yup.number().moreThan(0, 'Required').required("Required"),
    });

    const handleClose = () => setDialogStatus(false);

    const onSubmit = (values) => {
        const payload = {...values};
        delete payload.gstRate;
        setSavingStatus(true);
        voucherServices.save(payload).then(res => {
            AppAlert(res.msg);
            setDialogStatus(false);
            if (!data) navigate("/voucher/expense/details/" + res.id);
            if (callBack) callBack();
            setDialogStatus(false);
        }).catch(error => {
            AppAlert(error.msg || "Error", "error");
        }).finally(() => setSavingStatus(false));
    };

    return (
        <>

            {!data ?
                <Tooltip title="Add New Expense">
                    <button type="button" className='btn btn-default w-100 text-start' onClick={addNew}>
                        <Add /> Expense Voucher
                    </button>
                </Tooltip>
                :
                <Tooltip title="Update Expense ">
                    <span type="button" className='btn btn-primary' onClick={updateData}>
                        <Edit/> Update Expense 
                    </span>
                </Tooltip>
            }

            <Dialog open={dialogStatus} onClose={handleClose} fullWidth maxWidth="md">
                <Formik
                    initialValues={formValues || initialValues}
                    validationSchema={validationRule}
                    onSubmit={onSubmit}
                    enableReinitialize
                >
                    {(formik) => (
                        <Form>
                            {/* --- MOUNT THE OBSERVER HERE --- */}
                            <ExpenseCalculationObserver />

                            <DialogTitle className="d-flex justify-content-between align-items-center bg-light">
                                <span>Expense Voucher Entry</span>
                                <IconButton onClick={handleClose} size="small"><Close /></IconButton>
                            </DialogTitle>
                            
                            <DialogContent dividers>
                                {data && 
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <FormikControl control="input" formik={formik} type="text" label="Voucher No" name="voucherNo" />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <FormikControl control="input" formik={formik} type="date" label="Voucher Date" name="voucherDate" />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <FormikControl control="input" formik={formik} type="text" label="Ref/Bill No" name="refNo" />
                                        </div>
                                    </div>
                                }

                                <div className='row'>
                                    <div className="col-md-6 mb-3">
                                        <FormikControl control="input" autoFocus formik={formik} type="text" label="Spent By (Employee)" name="employeeName" />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <FormikControl control="autoselect" formik={formik} label="Project" name="locationId" options={companyLocationDD || []} />
                                    </div>

                                    <div className="col-12"><hr /></div>

                                    <div className="col-md-6 mb-3">
                                        <FormikControl control="autoselect" formik={formik} label="Expense Category" name="ledgerId" options={expenseLedgers} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <FormikControl control="autoselect" formik={formik} label="Paid From" name="paymentLedgerId" options={bankCashLedgers} />
                                    </div>

                                    <div className="col-md-3 mb-3">
                                        <FormikControl control="input" formik={formik} type="number" label="Amount (Taxable)" name="taxableTotal" />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        
                                        <FormikControl control="input" formik={formik} type="number" label="GST Rate %" name="gstRate" inputProps={{ min: 0, max: 100 }} />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <FormikControl control="input" formik={formik} type="text" label="Round Off" name="roundOff" readOnly />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <TextField id="outlined-basic" label="Grand Total" variant="outlined" value={formik.values.grandTotal}  />
                                        {/*<FormikControl control="input" formik={formik} type="number" label="Grand Total" name="grandTotal" readOnly className="bg-light fw-bold" />*/}
                                    </div>

                                    <div className="col-md-12">
                                        <FormikControl control="input" formik={formik} type="text" multiline rows={2} label="Narration" name="narration" />
                                    </div>
                                </div>

                                <div className="mt-3">
                                    {savingInProgress ? <LinearProgress /> : (
                                        <div className="d-flex justify-content-end gap-2">
                                            <button className="btn btn-light" type="button" onClick={handleClose}>Cancel</button>
                                            <button className="btn btn-primary px-4" type="submit">Save Expense</button>
                                        </div>
                                    )}
                                </div>
                            </DialogContent>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </>
    );
}

export default ExpenseVoucherForm;